import moment from 'moment';

import { Link, Switch, Route, withRouter } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';

import { useRouteMatch } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';

import DbApi from 'api/db';
import DatasetsApi from 'api/dataset';
import DbHeader from 'components/dbs/DbHeader';
import AlertHelper from 'helpers/AlertHelper';
import NewDatasetPage from './NewDatasetPage';
import EditDatasetPage from './EditDatasetPage';
import DataGridWithPagination from 'components/ui/DataGrid/DataGridWithPagination';

const DbPage = ({ match }) => {
  const { t } = useTranslation();
  const routeMatch = useRouteMatch();
  const history = useHistory();

  const [state, setState] = useState({
    fetching: true,
    datasets: {},
    page: 1,
    pageSize: 10,
    order: [['created_at', 'asc']],
    search: null,
    rows: [],
    db: {},
    sortColumns: [],
    changed: true,
    modalIsOpen: false,
  });

  useEffect(() => {
    if (!state.changed) {
      return;
    }
    fetchDb();
    fetchDatasets();
  }, [state.page, state.search, state.order, state.pageSize, state.changed]);

  const fetchDatasets = async () => {
    setState(prevState => {
      return { ...prevState, fetching: true };
    });

    try {
      const { dbId } = routeMatch.params;
      const { page, search, pageSize, order } = state;
      const datasets = await DbApi.getDatasetsByPage(dbId, { page, search, pageSize, order });

      const rows = updateRows(datasets);
      setState(prevState => {
        return { ...prevState, datasets, rows };
      });
    } finally {
      setState(prevState => {
        return { ...prevState, fetching: false, changed: false };
      });
      history.push(match.url);
    }
  };

  const updateRows = ({ rows }) => {
    const { search } = state;
    const filtered_data = search ? rows.filter(row => JSON.stringify(row).toLowerCase().includes(search.toLowerCase())) : rows || [];

    return filtered_data.map(item => {
      return {
        _id: item.id,
        ...item.data,
        _createdAt: item.createdAt,
      };
    });
  };

  const fetchDb = async () => {
    setState(prevState => {
      return { ...prevState, fetching: true };
    });

    try {
      const { dbId } = routeMatch.params;
      const db = await DbApi.get(dbId);
      setState(prevState => {
        return { ...prevState, db };
      });
    } finally {
      setState(prevState => {
        return { ...prevState, fetching: false };
      });
    }
  };

  const queryClient = useQueryClient();

  const mutationParams = {
    onSuccess: () => {
      queryClient.invalidateQueries('datasets');
    },
  };

  const dateFormatter = ({ column, row }) => {
    const date_value = row[column.key];

    if (!date_value) {
      return '--';
    }

    return moment(date_value).format('DD/MM/YYYY');
  };

  const dateTimeFormatter = ({ column, row }) => {
    const date_value = row[column.key];

    if (!date_value) {
      return '--';
    }

    return moment(date_value).format('DD/MM/YYYY HH:mm');
  };

  const formatters = {
    default: ({ column, row }) => row[column.key] || null,
    date: dateFormatter,
    password: () => {
      return '*********';
    },
  };

  const columnFormatter = ({ column, row }) => {
    const props = properties[column.key] || {};

    const format = props.format || 'default';
    const formatFn = formatters[format] || formatters['default'];

    return formatFn({ column, row });
  };

  const deleteMutation = useMutation(id => DatasetsApi.delete(id), mutationParams);

  const deleteDataset = async item => {
    const result = await AlertHelper.confirmDanger({
      title: t('dialogs.remove.title'),
      text: t('dialogs.remove.description'),
    });

    if (result.value) {
      deleteMutation.mutate(item._id);

      setTimeout(async () => {
        setState(prevState => {
          return { ...prevState, changed: true };
        });
      }, 300);
    }
  };

  const db = state.db;
  const schema = db.schema || {};
  const properties = schema.properties || {};
  const db_count = state.datasets.count || state.datasets.length;

  const defaultColumnProperties = {
    resizable: true,
  };

  const dynamic_columns = [];
  const fixed_columns = [{ key: '_createdAt', name: t('columns.created_at'), width: 'auto', formatter: dateTimeFormatter, sortable: true }];

  // fixed_columns.push({ key: '_empty', name: '', width: 'auto', resizable: true });

  fixed_columns.push({
    key: 'actions',
    name: 'Ações',
    width: '120px',
    formatter: ({ row }) => {
      return (
        <div className="d-flex">
          <Link className="btn btn-icon btn-sm" to={`/databases/${db.id}/edit/${row._id}`}>
            <FiEdit2 size={16} />
          </Link>

          <button className="btn btn-icon btn-sm ms-3" onClick={deleteDataset.bind(this, row)}>
            <FiTrash2 size={16} />
          </button>
        </div>
      );
    },
    sortable: false,
  });

  for (const key in properties) {
    dynamic_columns.push({ key, name: properties[key].title || key, sortable: true, formatter: columnFormatter });
  }

  const columns = [...dynamic_columns, ...fixed_columns].map(c => ({ ...c, ...defaultColumnProperties }));

  const rows = state.rows || [];

  const handlePageChange = async page => {
    setState(prevState => {
      return { ...prevState, page, changed: true };
    });
  };

  const onSearch = search => {
    setState(prevState => {
      return { ...prevState, search, page: 1, changed: true };
    });
  };

  const onSortColumnsChange = ([sortColumn = {}]) => {
    const { columnKey, direction } = sortColumn;

    if (!columnKey && !direction) {
      setState(prevState => {
        return { ...prevState, order: [['created_at', 'asc']], page: 1, sortColumns: [], changed: true };
      });
      return;
    }

    const order = [[`data.${columnKey}`, direction.toLowerCase()]];

    setState(prevState => {
      return { ...prevState, order, page: 1, sortColumns: [sortColumn], changed: true };
    });
  };

  const onChangePageSize = ({ value }) => {
    setState(prevState => {
      return { ...prevState, page: 1, pageSize: value, changed: true };
    });
  };

  const { page: current_page, pageSize, sortColumns } = state;
  return (
    <>
      <DbHeader db={db} onSearch={onSearch} />

      <div className="container mt-0">
        <DataGridWithPagination
          grid={{ columns, rows }}
          pagination={{ current_page, count: db_count, onPageChange: handlePageChange, pageSize }}
          loading={state.fetching}
          sortColumns={sortColumns}
          onSortColumnsChange={onSortColumnsChange}
          onChangePageSize={onChangePageSize}
        />
      </div>
      <Switch>
        <Route exact path="/databases/:dbId/add" children={<NewDatasetPage db={db} backUrl={match.url} setState={setState} />} />
        <Route exact path="/databases/:dbId/edit/:datasetId" children={<EditDatasetPage db={db} backUrl={match.url} setState={setState} />} />
      </Switch>
    </>
  );
};

export default withRouter(DbPage);
